import { useEffect, useState } from 'react';
import { Linking, Platform } from 'react-native';

import LS from '../../utils/localStorageUtils';

/**
 * The referral code a new player arrived with, held until they register.
 *
 * The inviter shares `<inviteBaseUrl>/<code>` (e.g. https://svara.com/invite/AB12CD, or
 * http://localhost:4323/invite/AB12CD locally). Opening it captures the code; registration and
 * social login then send it as `invitedBy`, and the backend attributes the account - only if the
 * account is new, which is also why the code is dropped after any successful sign-in. Attribution
 * itself is always server-side; this only carries the code across the app's first screens.
 */
const STORAGE_KEY = 'pending_invite_code';
/** An invite that was opened but not acted on this long ago no longer counts. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const CODE_PATTERN = /^[A-Za-z0-9_-]{4,64}$/;

type StoredCode = { code: string; capturedAt: number };

/** `/invite/<code>` anywhere in the path, or a `?ref=` / `?invite=` query parameter. */
export const parseInviteCode = (url: string | null | undefined): string | null => {
	if (!url) return null;

	const pathMatch = url.match(/\/invite\/([^/?#]+)/i);
	const queryMatch = url.match(/[?&](?:ref|invite)=([^&#]+)/i);
	const raw = pathMatch?.[1] ?? queryMatch?.[1];
	if (!raw) return null;

	let code: string;
	try {
		code = decodeURIComponent(raw).trim();
	} catch {
		return null;
	}
	return CODE_PATTERN.test(code) ? code : null;
};

export const savePendingInviteCode = (code: string) => {
	LS.set(STORAGE_KEY, JSON.stringify({ code, capturedAt: Date.now() } satisfies StoredCode));
};

export const getPendingInviteCode = async (): Promise<string | null> => {
	const raw = await LS.getAsync(STORAGE_KEY);
	if (!raw) return null;

	try {
		const stored = JSON.parse(raw) as StoredCode;
		if (!stored?.code || Date.now() - stored.capturedAt > MAX_AGE_MS) {
			clearPendingInviteCode();
			return null;
		}
		return stored.code;
	} catch {
		clearPendingInviteCode();
		return null;
	}
};

export const clearPendingInviteCode = () => {
	LS.remove(STORAGE_KEY);
};

const captureFromUrl = (url: string | null) => {
	const code = parseInviteCode(url);
	if (code) {
		savePendingInviteCode(code);
	}
	return code;
};

/** Set once the first-launch install check has run, so it never repeats (or re-prompts on iOS). */
const DEFERRED_CHECKED_KEY = 'invite_deferred_checked';

/**
 * A friend who did not have the app went through the store, so the link itself never reached the
 * app. On the first launch after install the code is recovered from what the landing page left:
 *
 * - Android: the Play Store hands the `referrer` the landing page put on the store URL
 *   (`invite_code=<code>`) to the Install Referrer API.
 * - iOS has no equivalent, so the landing page copies the invite link before opening the App Store
 *   and the app looks for it on the pasteboard. `hasURL` peeks without the paste prompt; only when
 *   a URL is there does the app read it, which shows iOS's own "pasted from" notice.
 *
 * The check runs once per install and never overrides a code that arrived by deep link.
 */
const captureDeferredInviteCode = async () => {
	if (await LS.getAsync(DEFERRED_CHECKED_KEY)) return;
	LS.set(DEFERRED_CHECKED_KEY, '1');
	if (await getPendingInviteCode()) return;

	try {
		if (Platform.OS === 'android') {
			const { getInstallReferrer } = require('react-native-device-info');
			// e.g. "invite_code=AB12CD&utm_source=google-play", sometimes still URL-encoded.
			const referrer = decodeURIComponent((await getInstallReferrer()) ?? '');
			const code = referrer.match(/(?:^|&)invite_code=([^&]+)/)?.[1];
			if (code) captureFromUrl(`?invite=${code}`);
			return;
		}

		if (Platform.OS === 'ios') {
			const mod = require('@react-native-clipboard/clipboard');
			const Clipboard = mod?.default ?? mod;
			if (await Clipboard.hasURL?.()) {
				captureFromUrl(await Clipboard.getString());
			}
		}
	} catch {
		// No referrer / no pasteboard access: the player can still type the code at sign-in.
	}
};

/**
 * Captures an invite code from wherever the app was opened from.
 *
 * - Web: the page URL (`/invite/<code>`). The path is replaced with `/`, since web routing ignores
 *   URLs and a reload should not re-capture. Returns the code so the web entry can show the
 *   invite landing on phones.
 * - Native: the deep link that launched the app or arrives while it runs (`svara://invite/<code>`,
 *   or the https invite link once App Links / Universal Links are set up), and on first launch the
 *   store install path above.
 */
export const useInviteLinkCapture = () => {
	const [webCode, setWebCode] = useState<string | null>(null);

	useEffect(() => {
		if (Platform.OS === 'web') {
			if (typeof window === 'undefined') return;
			const code = captureFromUrl(window.location.href);
			if (code) {
				window.history.replaceState(null, '', '/');
				setWebCode(code);
			}
			return;
		}

		Linking.getInitialURL()
			.then(captureFromUrl)
			.catch(() => undefined)
			.finally(() => {
				captureDeferredInviteCode();
			});
		const subscription = Linking.addEventListener('url', ({ url }) => captureFromUrl(url));
		return () => subscription.remove();
	}, []);

	return { webCode, dismissWebCode: () => setWebCode(null) };
};
